import { AuthService } from '../../services/auth/auth.service';
import { inject, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BusinessFieldDiff, PendingSellerChange } from '../../models/pending-seller-change.model';
import { PendingSellerChangeService } from '../../services/pending-seller-change.service';
import { UserRoleService } from '../../services/user-role.service';

@Component({
  selector: 'app-pending-seller-changes',
  templateUrl: './pending-seller-changes.component.html',
  styleUrls: ['./pending-seller-changes.component.css'],
  standalone: false
})
export class PendingSellerChangesComponent implements OnInit, OnChanges {
  readonly staffAuth = inject(AuthService);
  /** When set (business page), only this business's changes are listed. */
  @Input() businessId?: string;

  /**
   * View-only: the list and its details stay readable but Approve and Reject are off. The
   * business page sets it until the admin takes the business's edit lease.
   */
  @Input() readOnly = false;

  readonly pageSize = PendingSellerChangeService.PAGE_SIZE;

  isLoading = false;
  changes: PendingSellerChange[] = [];
  page = 1;
  totalCount = 0;
  totalPages = 1;
  errorMessage = '';
  rejectionDialogOpen = false;
  rejectionReason = '';
  changeToReject: PendingSellerChange | null = null;
  /** Change whose before/after detail row is expanded. */
  expandedChangeId: string | null = null;
  /** Resolved display names for submitter user ids. */
  readonly userNames = new Map<string, string>();
  private readonly resolvingUserIds = new Set<string>();

  constructor(
    private readonly service: PendingSellerChangeService,
    private readonly userRoleService: UserRoleService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId'] && !changes['businessId'].firstChange) {
      this.page = 1;
      this.load();
    }
    // Back to view-only: a half-written rejection belongs to edit mode.
    if (changes['readOnly'] && this.readOnly && this.rejectionDialogOpen) {
      this.cancelReject();
    }
  }

  setPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.page = page;
    this.load();
  }

  toggleDetails(change: PendingSellerChange) {
    this.expandedChangeId = this.expandedChangeId === change.id ? null : change.id;
  }

  getDiffs(change: PendingSellerChange): BusinessFieldDiff[] {
    return change.diffs ?? [];
  }

  approve(change: PendingSellerChange) {
    if (!this.staffAuth.can('Marketplace.SellerChanges.Moderate')) return;
    this.errorMessage = '';
    this.service.approveChange(change.id).subscribe({
      next: () => this.load(),
      error: err => (this.errorMessage = err?.error?.message || err?.message || 'Approving the change failed.')
    });
  }

  reject(change: PendingSellerChange) {
    if (!this.staffAuth.can('Marketplace.SellerChanges.Moderate')) return;
    this.changeToReject = change;
    this.rejectionDialogOpen = true;
    this.rejectionReason = '';
  }

  confirmReject() {
    if (!this.staffAuth.can('Marketplace.SellerChanges.Moderate')) return;
    if (this.changeToReject && this.rejectionReason.trim()) {
      this.errorMessage = '';
      this.service.rejectChange(this.changeToReject.id, this.rejectionReason.trim()).subscribe({
        next: () => this.load(),
        error: err => (this.errorMessage = err?.error?.message || err?.message || 'Rejecting the change failed.')
      });
      this.cancelReject();
    }
  }

  cancelReject() {
    this.rejectionDialogOpen = false;
    this.changeToReject = null;
    this.rejectionReason = '';
  }

  private load() {
    this.isLoading = true;
    this.service.getPendingChanges(this.page, this.businessId).subscribe({
      next: result => {
        this.isLoading = false;
        this.changes = result?.items ?? [];
        this.totalCount = result?.total ?? 0;
        this.totalPages = Math.max(Math.ceil(this.totalCount / this.pageSize), 1);
        // Reviewing the last row of the last page shrinks the list; snap back.
        if (this.page > this.totalPages) {
          this.page = this.totalPages;
          this.load();
          return;
        }
        this.resolveSubmitterNames(this.changes);
      },
      error: err => {
        this.isLoading = false;
        this.changes = [];
        this.errorMessage = err?.error?.message || err?.message || 'Loading pending business changes failed.';
      }
    });
  }

  /** SubmittedBy is the seller's user id; show the name, linked to /profile/:userId. */
  private resolveSubmitterNames(changes: PendingSellerChange[]) {
    const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ids = new Set(
      changes
        .map(c => c.submittedBy)
        .filter((id): id is string =>
          !!id && guid.test(id) && !this.userNames.has(id) && !this.resolvingUserIds.has(id))
    );
    ids.forEach(id => {
      this.resolvingUserIds.add(id);
      this.userRoleService.getUserById(id).subscribe({
        next: user => {
          this.resolvingUserIds.delete(id);
          const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email;
          if (name) {
            this.userNames.set(id, name);
          }
        },
        error: () => this.resolvingUserIds.delete(id)
      });
    });
  }
}
